<template>
  <section>
    <!-- User Summary Cards -->
    <KpiCards class="mb-3" :items="kpiItems" :loading="!userSummaryCards.length" :skeleton="4" />

    <data-table
      :headers="headers"
      :items="users"
      commit="SET_USERS"
      :viewItem="viewItem"
      :editItem="editItem"
      :deleteItem="deleteItem"
      :customActions="customActions"
      :selectable="true"
      :selectedItems="selectedUsers"
      @selection-change="onSelectionChange"
    >
      <div slot="activator" class="row align-center mb-4">
        <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4">
          <div class="d-flex align-center flex-wrap">
            <v-card-title class="py-0 pl-0 ps-products-title mr-2">
              {{ $t("users") }}
            </v-card-title>
            <div class="br-tabs">
              <div
                v-for="(val, i) in status_list"
                :key="i"
                class="br-tab"
                :class="{ 'br-tab--active': status_filter === val.value }"
                @click="status_filter = val.value"
              >
                {{ $t(val.text) }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-xs-12 col-sm-6 col-md-6 col-lg-3">
          <TextSelect
            :dense="true"
            :placeholder="false"
            :items="storeOptions"
            v-model="store_filter"
            text="name"
            itemValue="id"
            :returnObject="false"
            label="store"
          />
        </div>

        <div class="col-xs-12 col-sm-6 col-md-6 col-lg-2">
          <TextSearch
            v-model="search"
            @search="fetchItem()"
            @onClear="
              search = '';
              fetchItem();
            "
          />
        </div>
        <div class="col-xs-12 col-sm-12 col-md-12 col-lg-3 text-right">
          <!-- Bulk delete button (shown when users are selected) -->
          <v-btn
            v-if="selectedUsers.length > 0"
            depressed
            color="red"
            dark
            class="mr-4"
            @click="bulkDeleteUsers"
          >
            <v-icon left>fal fa-trash</v-icon>
            {{ $t("delete_selected") }} ({{ selectedUsers.length }})
          </v-btn>
          <!-- trash -->
          <v-btn
            depressed
            class="mr-4"
            @click="$router.push('/administrator/user-trash')"
          >
            <v-icon size="20">fal fa-trash</v-icon>
          </v-btn>

          <v-btn
            depressed
            color="primary"
            @click="newItem"
          >
            <v-icon left>fal fa-plus-circle</v-icon>
            {{ $t("add_new") }}
          </v-btn>
        </div>
      </div>
    </data-table>
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      :title="'user_details'"
      @submit="submitForm"
    >
      <div slot="activator">
        <v-row>
          <v-col cols="4">{{ $t("id") }}</v-col>
          <v-col cols="8">{{ form.id }}</v-col>
          <v-col cols="4">{{ $t("store_name") }}</v-col>
          <v-col cols="8">{{ form.stores.name }}</v-col>
          <v-col cols="4">{{ $t("first_name") }}</v-col>
          <v-col cols="8">{{ form.first_name }}</v-col>
          <v-col cols="4">{{ $t("phone_no") }}</v-col>
          <v-col cols="8">{{ form.phone_no }}</v-col>
          <v-col cols="4">{{ $t("email") }}</v-col>
          <v-col cols="8">{{ form.email }}</v-col>
          <v-col cols="4">{{ $t("role") }}</v-col>
          <v-col cols="8">
            <v-chip
              outlined
              :color="RoleColor(form.role)"
              small
              class="text-capitalize"
            >
              {{ $t(userRole(form.role)) }}</v-chip
            ></v-col
          >
          <v-col cols="4">{{ $t("is_verify") }}</v-col>
          <v-col cols="8">
            <v-chip
              outlined
              :color="form.is_active ? 'success' : 'grey'"
              small
              class="text-capitalize"
            >
              {{ form.is_active ? $t("active") : $t("inactive") }}</v-chip
            ></v-col
          >
          <v-col cols="4">{{ $t("date") }}</v-col>
          <v-col cols="8">{{ form.created_at | moment }}</v-col>
        </v-row>
      </div>
    </app-custom-dialog>
    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      :title="titleStatus"
      @submit="submitForm"
    >
      <div slot="activator">
        <TTextField
          label="first_name"
          :errorMessages="first_nameErrors"
          v-model="form.first_name"
          @input="$v.form.first_name.$touch()"
          @blur="$v.form.first_name.$touch()"
        />
        <TTextField
          label="last_name"
          v-model="form.last_name"
          :placeholder="$t('last_name_optional')"
        />
        <TTextField
          label="phone_no"
          :errorMessages="phone_noErrors"
          v-model="form.phone_no"
          @input="$v.form.phone_no.$touch()"
          @blur="$v.form.phone_no.$touch()"
        />
        <TTextField
          :errorMessages="emailErrors"
          label="email"
          v-model="form.email"
          @input="$v.form.email.$touch()"
          @blur="$v.form.email.$touch()"
        />

        <!-- Password fields only for new users -->
        <template v-if="!update">
          <t-password
            v-model="form.password"
            :errorMessages="passwordErrors"
            label="password"
            showKey="showPassword"
            @showPassword="showPassword = !showPassword"
            :type="showPassword"
          />
          <t-password
            v-model="form.confirm_password"
            @showConfirmPassword="showConfirmPassword = !showConfirmPassword"
            :errorMessages="confirm_passwordErrors"
            label="confirm_password"
            showKey="showConfirmPassword"
            :type="showConfirmPassword"
          />
        </template>

        <!-- Password change option for existing users -->
        <template v-else>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left>fal fa-key</v-icon>
                {{ $t("change_password") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <t-password
                  v-model="form.new_password"
                  label="new_password"
                  showKey="showNewPassword"
                  @showPassword="showNewPassword = !showNewPassword"
                  :type="showNewPassword"
                />
                <t-password
                  v-model="form.confirm_new_password"
                  label="confirm_new_password"
                  showKey="showConfirmNewPassword"
                  @showPassword="
                    showConfirmNewPassword = !showConfirmNewPassword
                  "
                  :type="showConfirmNewPassword"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </div>
    </app-custom-dialog>
    <!-- AppLoading -->
    <app-loading :overlay="overlay" />
  </section>
</template>

<script>
import DataTable from "@/components/DataTable";
import { mapGetters, mapActions } from "vuex";
import { userHead } from "@/utils/head";
import TTextField from "@/components/global/TextField";
import AppDetail from "@/components/products/details";
import { myMixin } from "@/mixin/main";
import TPassword from "@/components/global/TextPassword";
import TextSelect from "@/components/global/TextSelect";
import { success, confirm } from "@/controllers/alert";
import { CREATE_USER } from "@/validations/admin";
import Help from "@/utils/helper";

export default {
  mixins: [myMixin, CREATE_USER],
  data: () => ({
    status_filter: null,
    store_filter: null,
    stores: [],
    status_list: [
      {
        value: null,
        text: "all",
      },
      {
        value: 1,
        text: "active",
      },
      {
        value: 0,
        text: "inactive",
      },
    ],
    overlay: false,
    search: "",
    showPassword: false,
    showConfirmPassword: false,
    showNewPassword: false,
    showConfirmNewPassword: false,
    update: false,
    dialogAdd: false,
    dialog: false,
    selectedUsers: [],

    form: {
      stores: {},
      new_password: "",
      confirm_new_password: "",
    },
    userSummary: {
      cards: [],
      total_users: 0,
      active_users: 0,
      inactive_users: 0,
      users_without_stores: 0,
    },
  }),
  async created() {
    await this.fetchUserSummary();
    await this.fetchAllStores();
    await this.fetchItem();
  },
  watch: {
    status_filter() {
      this.fetchItem();
    },
    store_filter() {
      this.fetchItem();
    },
    users: {
      handler(newVal) {
        if (newVal && newVal.data && this.selectedUsers.length > 0) {
          const validIds = newVal.data.map((item) => item.id);
          this.selectedUsers = this.selectedUsers.filter((user) =>
            validIds.includes(user.id)
          );
        }
      },
      deep: true,
    },
  },
  components: {
    DataTable,

    TextSelect,
    TTextField,
    AppDetail,
    TPassword,
  },
  computed: {
    kpiItems() {
      return (this.userSummaryCards || []).map((item, index) => ({
        key: item.id ?? index,
        title: item.title,
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      users: "index/users",
      packages: "index/packages",
    }),

    storeOptions() {
      return [
        { id: null, name: this.$t("all_stores") || "All Stores" },
        ...this.stores,
      ];
    },

    titleStatus() {
      return this.update ? "update" : "add_new";
    },
    headers() {
      return userHead(this);
    },
    userSummaryCards() {
      const cards =
        this.userSummary && this.userSummary.cards
          ? this.userSummary.cards
          : [];
      return cards.slice(0, 4).map((card) => ({
        ...card,
        total: card.total !== undefined ? card.total : card.value || 0,
        description: card.description || card.title,
      }));
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    async fetchAllStores() {
      try {
        const { data } = await this.$http.get("get_all_store");
        if (data && data.status) {
          this.stores = data.data || [];
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    },
    async fetchUserSummary() {
      try {
        const { data } = await this.$http.get("admin_user_summary");
        if (data.status) {
          this.userSummary = data.data;
          // Ensure cards array exists
          if (!this.userSummary.cards) {
            this.userSummary.cards = [];
          }
        }
      } catch (error) {
        console.error("Error fetching user summary:", error);
        // Fallback to basic structure
        this.userSummary = {
          cards: [],
          total_users: 0,
          active_users: 0,
          inactive_users: 0,
          users_without_stores: 0,
        };
      }
    },

    async fetchItem() {
      let url = `admin_users?q=${this.search}&status=${this.status_filter}`;
      if (this.store_filter) {
        url += `&store_id=${this.store_filter}`;
      }
      const body = {
        that: this,
        url: url,
        commit: "SET_USERS",
      };
      await this.fetch(body);
      // Refresh summary after fetching users
      await this.fetchUserSummary();
    },
    async fetchPackage() {
      const body = {
        that: this,
        url: "packages",
        commit: "SET_PACKAGE",
      };
      await this.fetch(body);
    },

    async newItem() {
      await Help.clearForm(this.form, this);
      await this.fetchItem();
      await this.fetchPackage();
      this.update = false;
      this.dialogAdd = !this.dialogAdd;
    },
    editItem(user) {
      this.form = { ...user }; // Clone the user object
      this.update = true;
      this.dialogAdd = !this.dialogAdd;
    },

    async deleteItem(user) {
      // confirm delete user
      const { isConfirmed } = await confirm({
        props: this,
        text: "delete_user_confirm",
        con_txt: "delete",
        can_txt: "cancel",
        color_con: "#f44336",
        color_can: "#9e9e9e",
      });

      if (isConfirmed) {
        try {
          const body = {
            action: "delete",
            user_id: user.id,
          };
          const { data } = await this.$http.post("admin_users_action", body);
          if (data.status) {
            await this.fetchItem();
            await success(this, data.message);
          }
        } catch (error) {
          console.error("Error deleting user:", error);

          // Handle specific error messages
          if (error.response && error.response.data) {
            const errorMessage = error.response.data;
            if (
              errorMessage.includes("store_is_protected") ||
              errorMessage.includes("cannot_delete_user_store_protected")
            ) {
              // Show user-friendly error message
              this.$toast.error(this.$t("cannot_delete_user_store_protected"));
            } else {
              // Show generic error
              this.$toast.error(this.$t("error_occurred"));
            }
          }
        }
      }
    },

    async resetPassword(user) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "reset_password_confirm",
        textParams: {
          name: user.first_name || user.email,
        },
        con_txt: "reset_password",
        can_txt: "cancel",
        color_con: "#9c27b0",
        color_can: "#9e9e9e",
      });

      if (isConfirmed) {
        try {
          const body = {
            action: "reset_password",
            user_id: user.id,
          };
          const { data } = await this.$http.post("admin_users_action", body);
          if (data.status) {
            await success(this, data.message);
          }
        } catch (error) {
          console.error("Error resetting password:", error);
          if (error.response && error.response.data) {
            this.$toast.error(
              error.response.data.message || this.$t("error_occurred")
            );
          } else {
            this.$toast.error(this.$t("error_occurred"));
          }
        }
      }
    },
    async toggleUserBlock(user) {
      const action = user.is_active ? "block" : "unblock";
      const actionText = user.is_active ? "block" : "unblock";

      const { isConfirmed } = await confirm({
        props: this,
        text: "block_unblock_user_confirm",
        textParams: {
          action: this.$t(actionText),
        },
        con_txt: actionText,
        can_txt: "cancel",
        color_con: user.is_active ? "#ff9800" : "#4caf50",
        color_can: "#9e9e9e",
      });

      if (isConfirmed) {
        try {
          const body = {
            action: action,
            user_id: user.id,
          };
          const { data } = await this.$http.post("admin_users_action", body);
          if (data.status) {
            await this.fetchItem();
            await success(this, data.message);
          }
        } catch (error) {
          console.error(`Error ${action}ing user:`, error);
        }
      }
    },
    viewItem(item) {
      this.form = item;
      this.dialog = !this.dialog;
    },
    async submitForm() {
      try {
        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          let endpoint = "user_add";
          let message = "user_created_success";

          if (this.update) {
            endpoint = "user_update";
            message = "user_updated_success";
          }

          const { data } = await this.$http.post(endpoint, this.form);
          if (data.status) {
            this.dialogAdd = !this.dialogAdd;
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await success(this, data.message);
            this.update = false;
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },

    async editItem(item) {
      this.form = item;
      this.update = true;
      this.dialogAdd = !this.dialogAdd;
    },

    customActions(item) {
      return [
        {
          icon: "fal fa-eye",
          color: "blue",
          tooltip: this.$t("view_user_details"),
          action: () => this.viewItem(item),
        },
        {
          icon: "fal fa-edit",
          color: "green",
          tooltip: this.$t("edit_user"),
          action: () => this.editItem(item),
        },
        {
          icon: "fal fa-key",
          color: "purple",
          tooltip: this.$t("reset_password"),
          action: () => this.resetPassword(item),
        },
        {
          icon: item.is_active ? "fal fa-ban" : "fal fa-check-circle",
          color: item.is_active ? "orange" : "green",
          tooltip: item.is_active
            ? this.$t("block_user")
            : this.$t("unblock_user"),
          action: () => this.toggleUserBlock(item),
        },
        {
          icon: "fal fa-trash",
          color: "red",
          tooltip: this.$t("delete_user"),
          action: () => this.deleteItem(item),
        },
      ];
    },

    RoleColor(role) {
      const colors = {
        1: "primary", // admin
        2: "secondary", // manager
        3: "info", // staff
        4: "blue-grey", // user
      };
      return colors[role] || "grey";
    },

    userRole(role) {
      const roles = {
        1: "admin",
        2: "manager",
        3: "staff",
        4: "user",
      };
      return roles[role] || "unknown";
    },

    onSelectionChange(selectedItems) {
      this.selectedUsers = selectedItems;
    },

    async bulkDeleteUsers() {
      if (this.selectedUsers.length === 0) {
        return;
      }

      const { isConfirmed } = await confirm({
        props: this,
        text: "bulk_delete_users_confirm",
        textParams: {
          count: this.selectedUsers.length,
        },
        con_txt: "delete",
        can_txt: "cancel",
        color_con: "#f44336",
        color_can: "#9e9e9e",
      });

      if (isConfirmed) {
        try {
          this.overlay = true;
          const userIds = this.selectedUsers.map((user) => user.id);
          const { data } = await this.$http.post("admin_bulk_delete_users", {
            user_ids: userIds,
          });

          if (data.status) {
            this.selectedUsers = [];
            await this.fetchItem();
            // Additional safety: filter any remaining invalid selections
            if (this.users && this.users.data) {
              const validIds = this.users.data.map((item) => item.id);
              this.selectedUsers = this.selectedUsers.filter((user) =>
                validIds.includes(user.id)
              );
            }
            await success(this, data.message);
          } else {
            // Clear selection even if status is false
            this.selectedUsers = [];
          }
        } catch (error) {
          console.error("Error bulk deleting users:", error);
          if (error.response && error.response.data) {
            const errorMessage = error.response.data;
            if (
              errorMessage.includes("store_is_protected") ||
              errorMessage.includes("cannot_delete_user_store_protected")
            ) {
              this.$toast.error(this.$t("cannot_delete_user_store_protected"));
            } else {
              this.$toast.error(this.$t("error_occurred"));
            }
          }
          // Clear selection even on error to prevent stale state
          this.selectedUsers = [];
        } finally {
          this.overlay = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.ps-products-title {
  font-size: 1.25rem;
  font-weight: 900;
  color: #141021;
}
</style>
